// League configuration — divisions, rival clubs, fixtures and prizes.
// A season lasts 16 weeks: 1 preseason week, 14 matchdays (8 clubs,
// double round-robin) and a resolution week.

import { Rng, defaultRng, rollInt } from '@/game/utils/rng'

export const PLAYER_CLUB_ID = 'player'

export const SEASON = {
  clubsPerDivision: 8,
  matchdays: 14, // double round-robin with 8 clubs
  preseasonWeeks: 1,
  totalWeeks: 16,
  promotedCount: 2,
  relegatedCount: 2,
} as const

export interface DivisionDefinition {
  division: number // 4 = lowest (Cuarta), 1 = highest (Primera)
  name: string
  rivalRatingRange: [number, number]
  matchdayWinBonus: number // EUR per tie won
  seasonPrizeBase: number // champion's prize; scales down by position
  reputationPerWin: number
}

export const DIVISIONS: Record<number, DivisionDefinition> = {
  4: {
    division: 4,
    name: 'Cuarta Division Regional',
    rivalRatingRange: [25, 40],
    matchdayWinBonus: 200,
    seasonPrizeBase: 2000,
    reputationPerWin: 0.02,
  },
  3: {
    division: 3,
    name: 'Tercera Division Regional',
    rivalRatingRange: [40, 55],
    matchdayWinBonus: 500,
    seasonPrizeBase: 4000,
    reputationPerWin: 0.03,
  },
  2: {
    division: 2,
    name: 'Segunda Division Regional',
    rivalRatingRange: [55, 70],
    matchdayWinBonus: 1000,
    seasonPrizeBase: 8000,
    reputationPerWin: 0.04,
  },
  1: {
    division: 1,
    name: 'Primera Division Regional',
    rivalRatingRange: [70, 90],
    matchdayWinBonus: 2000,
    seasonPrizeBase: 15000,
    reputationPerWin: 0.06,
  },
}

export const LOWEST_DIVISION = 4
export const HIGHEST_DIVISION = 1

const CLUB_NAMES = [
  'Padel Indoor Norte', 'CD Raqueta de Oro', 'Club Padel Mirasol',
  'Padel Center Sur', 'CP La Bandeja', 'Padel Arena Levante',
  'Club El Smash', 'Padel Olimpo', 'CD Vibora Azul',
  'Padel Las Torres', 'Club Tres Paredes', 'CP El Globo',
  'Padel Costa Brava', 'CD Punto de Oro', 'Padel Hills',
  'Club La Volea', 'Padel Metropolitan', 'CP Cristal',
  'Padel Riverside', 'CD Los Halcones', 'Padel Premium 360',
  'Club Doble Pared', 'CP Mediterraneo', 'Padel City Center',
  'CD Estrella Padel', 'Padel Valle Verde', 'Club Match Point',
  'CP Los Pinos', 'Padel Urban Club', 'CD Rincon del Padel',
  'Padel Sunset', 'Club Gran Slam', 'CP Las Palmeras',
  'Padel Fortaleza', 'CD Aguila Real', 'Padel Pacifico',
  'Club Remate Final', 'CP Monte Alto', 'Padel Diamante',
  'CD Bola de Oro',
]

export interface RivalClub {
  id: string
  name: string
  rating: number // club strength 1-100, evolves between seasons
  form: number
}

export interface TieResult {
  homeMatchWins: number // matches won by home club (best of 3)
  awayMatchWins: number
  winnerId: string
}

export interface Fixture {
  matchday: number // 1-14
  homeId: string
  awayId: string
  result: TieResult | null
}

export interface LeagueState {
  season: number // 1, 2, 3...
  division: number // 4..1
  clubs: RivalClub[] // 7 AI rivals + the player club entry
  fixtures: Fixture[]
  seasonStartWeek: number // matchday N resolves at week seasonStartWeek + N
  phase: 'preseason' | 'regular' | 'finished'
}

export interface LeagueHistoryEntry {
  season: number
  division: number
  position: number
}

/** Generate 7 unique AI rivals for a division */
export function generateRivals(division: number, rng: Rng = defaultRng): RivalClub[] {
  const def = DIVISIONS[division]
  const available = [...CLUB_NAMES]
  const rivals: RivalClub[] = []

  for (let i = 0; i < SEASON.clubsPerDivision - 1; i++) {
    const nameIndex = Math.floor(rng() * available.length)
    const [name] = available.splice(nameIndex, 1)
    rivals.push({
      id: `rival-${division}-${i}`,
      name,
      rating: rollInt(def.rivalRatingRange[0], def.rivalRatingRange[1], rng),
      form: rollInt(40, 60, rng),
    })
  }
  return rivals
}

/**
 * Double round-robin fixtures via the circle method.
 * With 8 clubs: 7 single-leg rounds, mirrored to 14 matchdays.
 */
export function generateRoundRobin(clubIds: string[]): Fixture[] {
  const n = clubIds.length
  const rounds = n - 1
  const fixtures: Fixture[] = []

  // Circle method: fix the first club, rotate the rest
  const rotation = [...clubIds]
  for (let round = 0; round < rounds; round++) {
    for (let i = 0; i < n / 2; i++) {
      const a = rotation[i]
      const b = rotation[n - 1 - i]
      // Alternate home/away so nobody plays every round at home
      const home = round % 2 === 0 ? a : b
      const away = round % 2 === 0 ? b : a
      fixtures.push({ matchday: round + 1, homeId: home, awayId: away, result: null })
    }
    // Rotate: keep rotation[0] fixed
    rotation.splice(1, 0, rotation.pop()!)
  }

  // Second leg: same pairings, swapped venues
  const firstLeg = [...fixtures]
  for (const f of firstLeg) {
    fixtures.push({
      matchday: f.matchday + rounds,
      homeId: f.awayId,
      awayId: f.homeId,
      result: null,
    })
  }

  return fixtures
}

/** Create a fresh league season in the given division */
export function createSeason(
  division: number,
  season: number,
  seasonStartWeek: number,
  rng: Rng = defaultRng,
  existingRivals?: RivalClub[],
): LeagueState {
  const rivals = existingRivals ?? generateRivals(division, rng)
  const clubIds = [PLAYER_CLUB_ID, ...rivals.map(r => r.id)]

  return {
    season,
    division,
    clubs: rivals,
    fixtures: generateRoundRobin(clubIds),
    seasonStartWeek,
    phase: 'preseason',
  }
}

export interface StandingsRow {
  clubId: string
  name: string
  played: number
  won: number
  lost: number
  matchWinsFor: number
  matchWinsAgainst: number
  points: number
}

/** Compute the table from played fixtures (3 points per tie won) */
export function computeStandings(league: LeagueState, playerClubName: string): StandingsRow[] {
  const rows = new Map<string, StandingsRow>()
  const ensure = (clubId: string): StandingsRow => {
    if (!rows.has(clubId)) {
      const name = clubId === PLAYER_CLUB_ID
        ? playerClubName
        : league.clubs.find(c => c.id === clubId)?.name ?? clubId
      rows.set(clubId, {
        clubId, name, played: 0, won: 0, lost: 0,
        matchWinsFor: 0, matchWinsAgainst: 0, points: 0,
      })
    }
    return rows.get(clubId)!
  }

  // Make sure every club appears even before playing
  ensure(PLAYER_CLUB_ID)
  league.clubs.forEach(c => ensure(c.id))

  for (const fixture of league.fixtures) {
    if (!fixture.result) continue
    const home = ensure(fixture.homeId)
    const away = ensure(fixture.awayId)
    const { homeMatchWins, awayMatchWins, winnerId } = fixture.result

    home.played++
    away.played++
    home.matchWinsFor += homeMatchWins
    home.matchWinsAgainst += awayMatchWins
    away.matchWinsFor += awayMatchWins
    away.matchWinsAgainst += homeMatchWins

    if (winnerId === fixture.homeId) {
      home.won++
      home.points += 3
      away.lost++
    } else {
      away.won++
      away.points += 3
      home.lost++
    }
  }

  return [...rows.values()].sort((a, b) =>
    b.points - a.points ||
    (b.matchWinsFor - b.matchWinsAgainst) - (a.matchWinsFor - a.matchWinsAgainst) ||
    b.matchWinsFor - a.matchWinsFor
  )
}

/** Final position of a club in the league (1-8) */
export function getClubPosition(league: LeagueState, clubId: string, playerClubName = 'Mi Club'): number {
  const standings = computeStandings(league, playerClubName)
  return standings.findIndex(r => r.clubId === clubId) + 1
}

/** Season prize scaled by final position (champion gets the full base) */
export function getSeasonPrize(division: number, position: number): number {
  const def = DIVISIONS[division]
  const factor = Math.max(0, (SEASON.clubsPerDivision + 1 - position)) / SEASON.clubsPerDivision
  return Math.round(def.seasonPrizeBase * factor)
}
